import moment from 'moment-timezone';

import { GamePlayerStatus } from '../../../Constants';


// Wire-data key carrying the last-seen GamePlayer.last_action_uuid on
// every outgoing Click. Under CC_CLICK_NONCE_PROTOCOL the backend
// (engine/clicks/click_router.py) treats a stale parent_action_uuid as
// telemetry only — it logs the mismatch but does NOT reject. Mirrors the
// Python constant of the same name in click_router.py.
export const PARENT_ACTION_UUID = 'parent_action_uuid';

// CC_CLICK_NONCE_PROTOCOL. Wire-data key carrying a fresh
// client-generated nonce on every outgoing Click. The backend rejects
// any click_uuid it has already processed for that seat (true replays
// and double-deliveries) — duplicate detection is server-authoritative
// and requires no client pacing. A click that arrives with no click_uuid
// is rejected outright (no bypass). Mirrors the Python constant of the
// same name in click_router.py.
export const CLICK_UUID = 'click_uuid';


// Generate a fresh click nonce. Prefers crypto.randomUUID (all current
// browsers, and the Discord Activity target); falls back to an
// RFC4122-shaped v4 string for any runtime that lacks it (older browsers,
// some test environments). A click nonce needs uniqueness, not
// cryptographic strength, so the fallback's Math.random source is fine.
function generateClickUuid() {
    if (typeof crypto !== 'undefined'
            && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const rand = Math.floor(Math.random() * 16);
        const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
        return value.toString(16);
    });
}


// 2026-05-16: Mirror of engine/clicks/click_router.py CLICK_ROUTES.
// Any Click package sent while the seat's status isn't in this set
// gets a backend "Invalid Click Message ... :Waiting :Off :None ..."
// log line and is dropped server-side. The frontend AI fires stale
// clicks all the time — usually because the seat flipped to WAITING
// between the AI deciding and the package leaving. Drop them on this
// side so the console isn't flooded with rejections.
const STATUSES_THAT_ACCEPT_CLICKS = new Set([
    GamePlayerStatus.PICK_DRAFT_ID,
    GamePlayerStatus.GHOSTED_ID,
    // 2026-05-16 (afternoon): Mirror the GHOST_STARTER_ID safety net
    // that click_router.py added the same day. The backend now
    // routes GHOST_STARTER_ID clicks through message_while_ghosted
    // and the AI STATUS_ROUTES maps GHOST_STARTER_ID →
    // artificialPlaceGhost. Without this entry the allow-list dropped
    // the AI's placeGhost click before it left the browser, leaving
    // the seat stuck at GHOST_STARTER_ID with attacking_ghosts > 0.
    GamePlayerStatus.GHOST_STARTER_ID,
    GamePlayerStatus.DEFENDING_ID,
    GamePlayerStatus.TAKING_TURN_ID,
    GamePlayerStatus.SPECIAL_ID,
    GamePlayerStatus.SCORING_ID,
    GamePlayerStatus.RESEARCHING_ID,
    GamePlayerStatus.LODGING_ID,
    GamePlayerStatus.ROBOTING_ID,
    GamePlayerStatus.RENOVATING_ID,
    GamePlayerStatus.ADVERTISING_ID,
    GamePlayerStatus.GHOSTING_ID,
    GamePlayerStatus.RANDOM_ID,
    GamePlayerStatus.GAME_SETUP_ID,
    // Manual 3.1 opening ghost placement: per-click placeGhost while
    // the seat is in OPENING_GHOST_PLACEMENT_ID. Engine routes these
    // through click_router → opening_ghost_placement_click.
    GamePlayerStatus.OPENING_GHOST_PLACEMENT_ID,
    // Manual §3 in-game faction ceremony: commitFactionChoice while
    // the seat is in CHOOSE_FACTION_ID. Engine routes these through
    // click_router → choose_faction_response.
    GamePlayerStatus.CHOOSE_FACTION_ID,
]);


export default class GamePlayerPackageSender {

    constructor(gamePlayerAPI) {
        this.gamePlayerAPI = gamePlayerAPI;
        this.gamePlayerController = gamePlayerAPI.gamePlayerController;
        this.game = gamePlayerAPI.gamePlayerController.game;

        this.userId = this.game.userId;

        this.sendPackagedMessage = this.gamePlayerController.game.app.state.authenticator.sendPackagedMessage;

        this.packageId = 0;
    }

    sendPackage = (typeName, packageValue) => {
        if (typeName === 'Click' && !this._statusAcceptsClicks()) {
            return;
        }
        if (typeName === 'Click') {
            // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: drop a click that is
            // byte-identical to the one just sent for this seat WHILE the
            // seat's decision lifecycle (status/turn_status/action_status)
            // has not advanced. The engaged AI advisor re-enters its update
            // loop on every intermediate per-seat patch the engine streams
            // during ONE action (actionStatus INIT->WAIT, robot_assignments
            // shuffles), and at a fast speed step each re-entry drives another
            // concur -> another click built off a stale chain head that the
            // engine rejects (G65 Research double-fire, G66 stale ActionGrid,
            // 2026-07-08). The AI routes are deterministic on gamePlayerData,
            // so a GENUINE next step of a multi-step action (place the next
            // ghost, assign the next robot) produces a DIFFERENT payload and
            // is never suppressed; only a re-fire of the exact same decision —
            // a true duplicate — is dropped. The lifecycle guard means a
            // legitimate later repeat (after the engine advances the seat)
            // still goes through. A reject for this seat clears the latch (see
            // ArtificialPlayer.rejectEvent) so the reject-retry re-send is
            // never mistaken for a duplicate. Fingerprint BEFORE _stampAction-
            // Chain adds the per-click uuids (which are unique by design).
            //
            // CC_AUTOPLAY_DEDUP_SILENT_DROP (2026-07-09): the drop above is a
            // DEAD END — a dropped click never reaches the server, so no
            // GamePlayer patch and no Reject come back, and the reject-retry
            // one-shot (ArtificialPlayer.rejectEvent -> retryArmed) is never
            // armed. On a Feai BOT seat (artificial_player > 2) there is no
            // human and no manual UI to recover, so a FALSE-positive drop
            // strands the seat permanently — the deterministic AI legitimately
            // re-emits an identical payload at an UNCHANGED decision lifecycle
            // (the residual hole REVISION 2 of CC_ENGAGED_ADVISOR_SINGLE_EXECUTE
            // admitted; the send-seam cannot tell that legit re-emit from a
            // stale duplicate, and only the engine — which sees game state —
            // can). The recovery guarantee this restores: a Feai bot's click is
            // NEVER silently dropped. It always reaches the engine, which owns
            // duplicate detection WITH recovery — the click_uuid nonce catches
            // true replays/double-deliveries and typed Rejects
            // (stale_target_commit_...) catch stale state-based duplicates, and
            // a Reject re-fires the seat via CC_FEAI_REJECT_RETRY. One threat
            // per layer (HSH_STYLE §Protocol Changes): the bot path must not
            // re-buy the engine's dedup with an unrecoverable dead end. The drop
            // stays scoped to human-driven seats (artificial_player null/<=2)
            // — the engaged-advisor double-fire it was built for only occurs on
            // the owner's OWN human seat (SpeedController engaged), where the
            // human seat's manual controls are always a recovery path.
            const isFeaiBot =
                this.gamePlayerController.gamePlayerData.artificial_player > 2;
            if (!isFeaiBot) {
                const fingerprint = this._clickFingerprint(packageValue);
                const lifecycle = this._decisionLifecycle();
                if (this.gamePlayerController._lastClickFingerprint
                            === fingerprint
                        && this.gamePlayerController._lastClickLifecycle
                            === lifecycle) {
                    return;
                }
                this.gamePlayerController._lastClickFingerprint = fingerprint;
                this.gamePlayerController._lastClickLifecycle = lifecycle;
            }
            this._stampActionChain(packageValue);
        }
        const message = this._makePackage(typeName, packageValue);
        this.sendPackagedMessage(message);
    };

    // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: a stable identity for the click's
    // meaning — its type + data, taken BEFORE the per-click uuids are stamped
    // on (those are unique on every emit and would defeat the compare). Two
    // clicks with the same fingerprint carry the same instruction.
    _clickFingerprint = (packageValue) => {
        return JSON.stringify([packageValue.type, packageValue.data || null]);
    };

    // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: the seat's decision lifecycle. While
    // this triple is unchanged the seat is on the same decision; once the
    // engine advances any of the three the seat is on a new decision and an
    // identical payload is legitimate again.
    _decisionLifecycle = () => {
        const d = this.gamePlayerController.gamePlayerData;
        return `${d.status}|${d.turn_status}|${d.action_status}`;
    };

    _stampActionChain = (packageValue) => {
        // Stamp both click-protocol fields on every outgoing Click
        // (CC_CLICK_NONCE_PROTOCOL).
        //
        // click_uuid: a fresh per-click nonce the server dedupes on —
        // catches replays and double-deliveries regardless of timing.
        //
        // parent_action_uuid: the last-seen action-chain head. Null until
        // the first GamePlayer patch arrives. The backend treats a stale
        // head as telemetry (logged, not rejected) — the pipelined FE
        // fires continuations off a lagging head by design.
        if (!packageValue.data) {
            packageValue.data = {};
        }
        const lastActionUuid =
            this.gamePlayerController.lastActionUuid;
        packageValue.data[PARENT_ACTION_UUID] =
            lastActionUuid !== undefined ? lastActionUuid : null;
        packageValue.data[CLICK_UUID] = generateClickUuid();
    };

    _statusAcceptsClicks = () => {
        const status =
            this.gamePlayerController.gamePlayerData.status;
        return STATUSES_THAT_ACCEPT_CLICKS.has(status);
    };

    _makePackage = (typeName, packageValue) => {
        ++this.packageId;
        let message = {};
        message.datetime = moment();
        message.user = this.userId;
        message.game = this.game.gameData.id;
        message.game_player = this.gamePlayerController.gamePlayerData.id;

        message.type = typeName;
        message.package = packageValue;

        return JSON.stringify(message);
    };
}
